import { html, nothing } from 'lit';
import { distributionMetadata, distributionValues } from '../types.js';
import { styleMap } from 'lit/directives/style-map.js';
import { register as _registerElement } from "../../../elements/one-ux-popout/register-element.js";
import { register as _registerElement2 } from "../../../elements/one-ux-icon/register-element.js";
_registerElement2("icon-5bee0cc8de0fb4d9faa6df970f7cec8d");
_registerElement("popout-e95b26b55bcbf117c64ac3a2246ab8cc");
type popoutEntry = {
  entry: distributionMetadata;
  value: number;
  unit?: string;
  percentage?: number;
};
export function DistributionPopout({
  name,
  unit,
  metadata,
  values,
  referenceElement,
  direction,
  alignment,
  hidePercentage
}: {
  name: string;
  unit?: string;
  metadata: distributionMetadata[];
  values: distributionValues;
  referenceElement: Element;
  direction?: 'vertical' | 'horizontal';
  alignment?: 'start' | 'center' | 'end';
  hidePercentage?: boolean;
}) {
  const getValue = (key: string) => values[key] || 0;
  const {
    includeMeta,
    excludeMeta
  } = getDistributionMetadataByExclude(metadata);
  const total = includeMeta.reduce((sum, {
    entry
  }) => sum + getValue(entry.key), 0);
  const showPercentage = !hidePercentage && total > 0;
  return html`
    <popout-e95b26b55bcbf117c64ac3a2246ab8cc
      aria-hidden="true"
      style="
        pointer-events: none;
        color: var(--one-ux-palette--brand-neutral-600);
        visibility: hidden;
        animation: 500ms displayPopout;
        animation-fill-mode: forwards;
      "
      direction=${direction || 'vertical'}
      alignment=${alignment || 'start'}
      .reference=${referenceElement}
    >
      <div class="one-ux-typography--body-variant-400" style="margin: 0 0 var(--one-ux-spacing--300) 0;">
        ${name} (${unit ? `${total} ${unit}` : total})
      </div>
      <div
        class="one-ux-typography--body-300"
        style=${styleMap({
    width: '300px',
    display: 'grid',
    'grid-template-columns': showPercentage ? 'auto 1fr auto auto' : 'auto 1fr auto',
    gap: 'var(--one-ux-spacing--300) var(--one-ux-spacing--200)',
    'white-space': 'nowrap',
    margin: '0'
  })}
      >
        ${includeMeta.map(entry => {
    const percentage = showPercentage ? Math.round(100 * entry.value / total) : undefined;
    return DistributionPopoutEntry({
      ...entry,
      percentage
    });
  })}
        ${excludeMeta?.length ? [Separator(), excludeMeta?.map(DistributionPopoutEntry)] : nothing}
      </div>

      <style>
        @keyframes displayPopout {
          99% {
            visibility: hidden;
          }
          100% {
            visibility: visible;
          }
        }
      </style>
    </popout-e95b26b55bcbf117c64ac3a2246ab8cc>
  `;
  function getDistributionMetadataByExclude(metadata: distributionMetadata[]) {
    return metadata.reduce<{
      includeMeta: popoutEntry[];
      excludeMeta: popoutEntry[];
    }>((acc, curr) => {
      const value = getValue(curr.key);
      if (value != null) {
        const entry = {
          entry: curr,
          value,
          unit
        };
        const prop = curr.exclude ? 'excludeMeta' : 'includeMeta';
        acc[prop].push(entry);
      }
      return acc;
    }, {
      includeMeta: [],
      excludeMeta: []
    });
  }
}
function DistributionPopoutEntry({
  entry,
  value,
  unit,
  percentage
}: popoutEntry) {
  return html`
    <icon-5bee0cc8de0fb4d9faa6df970f7cec8d
      size="200"
      icon=${entry.icon.name}
      set=${entry.icon.set}
      style=${styleMap({
    'align-self': 'baseline',
    color: entry.color
  })}
    ></icon-5bee0cc8de0fb4d9faa6df970f7cec8d>
    <div
      style="
        white-space: normal; 
        overflow-wrap: anywhere;"
    >
      ${entry.name}
    </div>
    <div
      class="one-ux-typography--body-variant-300"
      style="
        text-align: right;
        margin: 0;"
    >
      ${value} ${unit}
    </div>
    ${percentage == undefined ? nothing : html`
          <div
            style="
              text-align: right;
              color: var(--one-ux-palette--brand-neutral-400);"
          >
            (${percentage}%)
          </div>
        `}
  `;
}
function Separator() {
  return html`<hr
    style=${styleMap({
    'grid-column': '1 / -1',
    margin: 0,
    border: 'none',
    'border-top': '1px solid var(--one-ux-palette--brand-neutral-300)',
    width: '100%'
  })}
  />`;
}